import { FormattedMessage } from 'react-intl';
import { useEffect, useState, useRef } from 'react';
import { validateDateComplete, validateDateFormat, validateNotFuture } from '@odmonorepo/odfunctions';
import { LoadingAnimation, useWizardContext, FormWrapper, FormattedMessageDiv } from '@odmonorepo/core-components';
import { Stack } from 'react-bootstrap';
import { useApplication } from 'merchant-api';
import { isValidSSN } from 'merchant-components';
import { useAnalytics } from 'use-analytics';
import * as yup from 'yup';
import { AdditionalOwners } from './AdditionalOwners';
import { ControlPerson } from './ControlPerson';
import { checkoutSteps } from '../../CheckoutWizard';
import { getCheckoutState } from 'merchant-api/checkout';
import { FINCEN_PLACEMENT_FLAG, useOptimizelyVariation } from 'merchant-api/vendor/optimizely';

const SSN = 'SOCIAL_SECURITY_NUMBER';

const SSNSchema = yup
  .string()
  .required('formInput.additionalOwner.identifiers.value.error')
  .test('ssn-validation', 'formInput.additionalOwner.identifiers.value.invalid', function (value) {
    if (!value) return false;
    if (/x/i.test(value)) return false;
    return isValidSSN(value);
  });

const OwnerSchema = yup.object({
  firstName: yup.string().required('formInput.additionalOwner.firstName.error'),
  lastName: yup.string().required('formInput.additionalOwner.lastName.error'),

  addresses: yup.array().of(
    yup.object({
      city: yup.string().required('formInput.additionalOwner.addresses.city.error'),
      state: yup.string().required('formSelect.additionalOwner.addresses.state.error'),
    }),
  ),

  dateOfBirth: yup
    .string()
    .required('formInput.additionalOwner.dateOfBirth.error')
    .test('must be complete date', 'formInput.dateOfBirth.error', validateDateComplete)
    .test('must be valid date', 'formInput.dateOfBirth.error.invalidFormat', validateDateFormat)
    .test('must be in past', 'formInput.dateOfBirth.error.futureDate', validateNotFuture),

  identifiers: yup.array().of(
    yup.object({
      type: yup.string().default(SSN),
      value: SSNSchema,
    }),
  ),
});

export const FINCEN = () => {
  const { checkoutData, isLoadingCheckoutState } = getCheckoutState();
  const [isAdditionalOwnersCompleted, setIsAdditionalOwnersCompleted] = useState(checkoutData?.isFincenAdditionalOwnersPassed || false);
  const { applicationData } = useApplication();
  const analytics = useAnalytics();
  const { data: fincenPlacementVariation, isLoading: isLoadingFincenPlacementVariation } = useOptimizelyVariation({
    featureKey: FINCEN_PLACEMENT_FLAG,
    analytics: useAnalytics(),
  });
  const { nextStep } = useWizardContext<typeof checkoutSteps>();
  const sessionIdRef = useRef(crypto.randomUUID());
  const fincenOpenedAtRef = useRef(Date.now());
  const sessionId = sessionIdRef.current;
  const FINCENOpenedAt = fincenOpenedAtRef.current;
  const completedRef = useRef(false); //
  const lastStepRef = useRef<'Additional Owners' | 'Control Person'>('Additional Owners');
  lastStepRef.current = isAdditionalOwnersCompleted ? 'Control Person' : 'Additional Owners';

  useEffect(() => {
    analytics.track('fincen_step_started', { sessionId, placementVariant: fincenPlacementVariation });
    return () => {
      if (!completedRef.current) {
        analytics.track('fincen_step_abandoned', {
          sessionId,
          placementVariant: fincenPlacementVariation,
          lastStepReached: lastStepRef.current,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const FINCENSchema = yup.object({
    additionalOwner: yup.array().when('otherIndividual', {
      is: 'true',
      then: (schema) => schema.of(OwnerSchema),
      otherwise: (schema) => schema.notRequired(),
    }),
    otherIndividual: yup.string().oneOf(['true', 'false']).required('formInput.additionalOwner.option'),
    areYouControlPerson: isAdditionalOwnersCompleted
      ? yup.string().oneOf(['true', 'false'], 'formInput.additionalOwner.option').required('formInput.additionalOwner.option')
      : yup.string().notRequired(),
    controlPerson: yup.object().when('areYouControlPerson', {
      is: (v: string) => isAdditionalOwnersCompleted && v === 'false',
      then: () => OwnerSchema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  if (!applicationData || isLoadingCheckoutState || isLoadingFincenPlacementVariation)
    return (
      <Stack className="m-auto">
        <LoadingAnimation />
      </Stack>
    );

  const FINCENComponent = isAdditionalOwnersCompleted ? ControlPerson : AdditionalOwners;

  return (
    <FormWrapper schema={FINCENSchema} defaultValues={applicationData.data}>
      <Stack gap={6} style={{ maxWidth: '600px' }} className="mx-auto">
        <h2 className="text-secondary text-center fs-3">
          <FormattedMessage id="additionalInformation.title" />
        </h2>
        <FINCENComponent
          applicationData={applicationData}
          setIsAdditionalOwnersPassed={setIsAdditionalOwnersCompleted}
          isAdditionalOwnersPassed={isAdditionalOwnersCompleted}
          nextStep={nextStep}
          sessionId={sessionId}
          placementVariant={fincenPlacementVariation}
          analytics={analytics}
          fincenOpenedAt={FINCENOpenedAt}
          onStepComplete={(value: boolean) => {
            completedRef.current = value;
          }}
        >
          <div className="mx-auto mt-5 mb-5" style={{ maxWidth: '600px' }}>
            <FormattedMessageDiv className="fs-8" id="additionalInformation.correctInformationConfirmation" />
          </div>
        </FINCENComponent>
      </Stack>
    </FormWrapper>
  );
};
